import React, { useState } from 'react';
import { getUserImg } from "../userAssests";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faVolumeHigh, faSpinner, faFileText, faTimes } from '@fortawesome/free-solid-svg-icons';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkRehype from 'remark-rehype';
import { useAuthContext } from '../AuthProvider';
import settings from '../settings';
const { REACT_APP_BACKEND_URL } = settings;

interface SourceLink {
    source_path: string;
    document_uuid: string;
}

type Ai_Response_Widget_Prop = {
    AiResponseContent: string;
    AiResponseSources: SourceLink[];
};

interface TranscriptionData {
    coach_name: string;
    patient_name: string;
    call_date: string;
    conversation: Array<{ text: string; speaker: string }>;
}


export const AiResponseWidget = ({ AiResponseContent, AiResponseSources }: Ai_Response_Widget_Prop) => {
    const [recordingUrls, setRecordingUrls] = useState<Record<string, string | "null">>({});
    const [loadingRecordings, setLoadingRecordings] = useState<Record<string, boolean>>({});
    const [transcriptionData, setTranscriptionData] = useState<TranscriptionData | null>(null);
    const [loadingTranscription, setLoadingTranscription] = useState<string | null>(null);
    const [showTranscriptionPopup, setShowTranscriptionPopup] = useState<boolean>(false);
    const { loginWithMicrosoft } = useAuthContext();

    const fetchRecordingUrl = async (recordingName: string) => {
        setLoadingRecordings(prev => ({ ...prev, [recordingName]: true }));
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                loginWithMicrosoft();
                return [];
            }

            const response = await fetch(`${REACT_APP_BACKEND_URL}/recordings/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    "Authorization": `Bearer ${token}`,
                },
                body: JSON.stringify({ recording_path: recordingName }),
            });

            if (!response.ok) {
                if (response.status === 401) loginWithMicrosoft();
                throw new Error('Network response was not ok');
            }

            const data = await response.json();
            setRecordingUrls(prev => ({ ...prev, [recordingName]: data.recording_url }));
        } catch (error) {
            console.error(`Failed to fetch recording URL for ${recordingName}`, error);
        } finally {
            setLoadingRecordings(prev => ({ ...prev, [recordingName]: false }));
        }
    };

    const fetchTranscription = async (documentUuid: string) => {
        setLoadingTranscription(documentUuid);
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                loginWithMicrosoft();
                return [];
            }

            const response = await fetch(`${REACT_APP_BACKEND_URL}/recordings/transcription/${documentUuid}`, {
                credentials: "include",
                headers: { "Authorization": `Bearer ${token}` },
            });

            if (!response.ok) {
                if (response.status === 401) loginWithMicrosoft();
                throw new Error('Failed to fetch transcription');
            }

            const data = await response.json();

            // Update with full object
            const { coach_name, patient_name, call_date, conversation } = data;

            setTranscriptionData({
                coach_name,
                patient_name,
                call_date,
                conversation,
            });

            setShowTranscriptionPopup(true);
        } catch (error) {
            console.error('Failed to fetch transcription', error);
            setTranscriptionData(null);
        } finally {
            setLoadingTranscription(null);
        }
    };


    const responseContent = AiResponseContent;
    const uniqueSources = AiResponseSources?.length 
    ? Array.from(new Map(AiResponseSources.map(item => [item.source_path, item])).values()) 
    : [];

    return (
        <div className="group w-full text-gray-800 dark:text-gray-100 border-b border-black/10 dark:border-gray-700 dark:bg-gray-800 bg-gray-200 border-gray-400">
            <div className="flex p-4 gap-4 text-base md:gap-6 md:max-w-2xl lg:max-w-xl xl:max-w-3xl md:py-6 lg:px-0 m-auto">
                <div className="flex-shrink-0 space-y-1">
                    <div className="w-6">
                        <img alt="User" src={getUserImg()} />
                    </div>
                </div>
                <div className="flex flex-col flex-1 gap-1 md:gap-3">
                    <div className="flex flex-col gap-3">
                        <div className="min-h-6 flex flex-col space-y-2 break-words">
                            <Markdown className="prose dark:prose-invert" remarkPlugins={[remarkGfm]} rehypePlugins={[remarkRehype]}>
                                {responseContent}
                            </Markdown>

                            {uniqueSources.length > 0 && (
                                <div
                                    className={`max-h-[200px] overflow-y-auto`}
                                >
                                    <ul>
                                        {uniqueSources.map((source, index) =>
                                            source.source_path.endsWith(".txt") && (
                                                <li key={index}>
                                                    <div className="inline-flex items-center bg-white dark:bg-gray-700 shadow-lg rounded-lg p-2 mt-1">
                                                        <FontAwesomeIcon icon={faFileText} className="mr-2 text-blue-500" />
                                                        <span>{source.source_path}</span>
                                                    </div>
                                                </li>
                                            )
                                        )}
                                    </ul>
                                </div>
                            )}

                            {uniqueSources.length > 0 && (
                                <div
                                    className={`max-h-[200px] overflow-y-auto`}
                                >
                                    <ul className="flex flex-col items-start space-y-2">
                                        {uniqueSources.map((recording, index) =>
                                            recording.source_path.endsWith(".mp3") && (
                                                <li key={index} className="flex flex-col items-start space-y-2">
                                                    <div className="flex flex-col items-start bg-white dark:bg-gray-700 shadow-lg rounded-lg p-4">
                                                        <div className="flex items-center space-x-2">
                                                            <label>{recording.source_path}</label>
                                                            <button
                                                                onClick={() => fetchRecordingUrl(recording.source_path)}
                                                                disabled={loadingRecordings[recording.source_path] || Boolean(recordingUrls[recording.source_path])}
                                                                className="bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 rounded flex items-center"
                                                            >
                                                                {loadingRecordings[recording.source_path] ? <FontAwesomeIcon icon={faSpinner} spin size="lg" /> : <FontAwesomeIcon icon={faVolumeHigh} size="lg" />}
                                                            </button>
                                                            <button
                                                                onClick={() => fetchTranscription(recording.document_uuid)}
                                                                disabled={loadingTranscription === recording.document_uuid}
                                                                className="bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded flex items-center"
                                                            >
                                                                {loadingTranscription === recording.document_uuid ? <FontAwesomeIcon icon={faSpinner} spin size="lg" /> : <FontAwesomeIcon icon={faFileText} size="lg" />}
                                                            </button>
                                                        </div>
                                                        {recordingUrls[recording.source_path] && (
                                                            <audio controls src={recordingUrls[recording.source_path]} />
                                                        )}
                                                    </div>
                                                </li>
                                            )
                                        )}
                                    </ul>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {showTranscriptionPopup && transcriptionData && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg max-w-3xl w-full mx-4 md:mx-0 max-h-full overflow-y-auto mt-2">
                        <div className="flex justify-between items-center border-b border-gray-200 dark:border-gray-700 px-6 py-4">
                            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">Call Information</h2>
                            <button
                                onClick={() => setShowTranscriptionPopup(false)}
                                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 focus:outline-none"
                            >
                                <FontAwesomeIcon icon={faTimes} size="lg" />
                            </button>
                        </div>
                        <div className="px-6 py-4">
                            <div className="space-y-4">

                                {/* Info in two columns */}
                                <div className="grid grid-cols-2 gap-x-6 gap-y-4 text-sm">
                                    {/* Left column for Coach and Patient */}
                                    <div>
                                        <div className="flex space-x-2">
                                            <span className="font-medium text-gray-600 dark:text-gray-400">Coach:</span>
                                            <span className="text-gray-800 dark:text-gray-200">{transcriptionData.coach_name}</span>
                                        </div>
                                        <div className="flex space-x-2 mt-2">
                                            <span className="font-medium text-gray-600 dark:text-gray-400">Patient:</span>
                                            <span className="text-gray-800 dark:text-gray-200">{transcriptionData.patient_name}</span>
                                        </div>
                                    </div>

                                    {/* Right column for Call Date */}
                                    <div>
                                        <div className="flex space-x-2">
                                            <span className="font-medium text-gray-600 dark:text-gray-400">Call Date:</span>
                                            <span className="text-gray-800 dark:text-gray-200">
                                                {transcriptionData.call_date.slice(0, 4)}-{transcriptionData.call_date.slice(4, 6)}-{transcriptionData.call_date.slice(6, 8)}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <hr className="mt-4 border-gray-700 dark:border-gray-700" />


                                {/* Display the conversation */}
                                {transcriptionData.conversation.map((item, index) => (
                                    <div
                                        key={index}
                                        className={`p-4 rounded-lg shadow-md ${
                                            index % 2 === 0 ? 'bg-gray-700' : 'bg-gray-700'
                                        }`}
                                    >
                                        <h3
                                            className={`font-semibold ${
                                                index % 2 === 0 ? 'text-blue-400' : 'text-green-400'
                                            }`}
                                        >
                                            {item.speaker}
                                        </h3>
                                        <p className="mt-1 text-gray-300 text-sm">
                                            {item.text}
                                        </p>
                                    </div>
                                ))}

                            </div>
                        </div>
                        <div className="flex justify-end border-t border-gray-200 dark:border-gray-700 px-6 py-4">
                            <button
                                onClick={() => setShowTranscriptionPopup(false)}
                                className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
